// Modal UX screens: pause menu.
//
// The menu is intentionally minimal — a small column of buttons with
// a fade-in + scale-in entry animation. Volume / colorblind / themes
// all live in the Settings modal now (use the ⚙ Settings button to
// reach them). Tutorial-replay + Switch Team + Leave Match + Copy
// Invite stay here because they're match-context actions, not
// preferences.
//
// Attack-ring suppression: while the menu is open we add the
// `smp-pause-open` body class. A small one-shot stylesheet hides any
// element tagged `.smp-attack-ring` so the player's circular attack
// indicator doesn't overlap the menu card.

const MENU_ANIM_MS = 180;
const MENU_CSS_ID = 'smp-pause-menu-css';
function ensurePauseCss(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(MENU_CSS_ID)) return;
  const s = document.createElement('style');
  s.id = MENU_CSS_ID;
  s.textContent = `
    body.smp-pause-open .smp-attack-ring { opacity: 0 !important; }
  `;
  document.head.appendChild(s);
}

const MODAL_BASE = `
  position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%) scale(.96);
  background: rgba(8,12,20,.95); backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 18px; padding: 24px 30px;
  font: 600 13px system-ui, sans-serif; color: #e2e8f0;
  box-shadow: 0 30px 80px rgba(0,0,0,.7);
  display: none;
  pointer-events: auto;
  max-width: 90vw; max-height: 85vh; overflow: auto;
  z-index: 150;
  opacity: 0;
  transition: opacity ${MENU_ANIM_MS}ms ease-out, transform ${MENU_ANIM_MS}ms cubic-bezier(.18,1.3,.4,1);
`;

export class PauseMenu {
  el: HTMLDivElement;
  onResume: () => void = () => undefined;
  onTutorial: () => void = () => undefined;
  onSwitchTeam: () => void = () => undefined;
  onSettings: () => void = () => undefined;
  onGoToLobby: () => void = () => undefined;
  onCopyInvite: () => void = () => undefined;

  constructor(parent: HTMLElement) {
    ensurePauseCss();
    this.el = document.createElement('div');
    this.el.className = 'ui interactive';
    this.el.style.cssText = MODAL_BASE + 'width: 320px;';
    this.el.innerHTML = `
      <div style="font:800 24px system-ui;margin-bottom:18px;">☰ Menu</div>
      <button id="pause-settings" style="display:block;width:100%;margin-bottom:8px;background:rgba(253,224,71,.1);color:#fde047;border:1px solid #fde047;padding:10px;border-radius:8px;font:800 12px system-ui;cursor:pointer;letter-spacing:.5px;">⚙ Settings</button>
      <button id="pause-invite" style="display:block;width:100%;margin-bottom:8px;background:rgba(134,239,172,.12);color:#86efac;border:1px solid #22c55e;padding:10px;border-radius:8px;font:800 12px system-ui;cursor:pointer;letter-spacing:.5px;">📎 Copy Invite Link</button>
      <button id="pause-lobby" style="display:block;width:100%;margin-bottom:8px;background:rgba(96,165,250,.12);color:#93c5fd;border:1px solid #60a5fa;padding:10px;border-radius:8px;font:800 12px system-ui;cursor:pointer;letter-spacing:.5px;">🏠 Leave Match</button>
      <button id="pause-switch" style="display:block;width:100%;margin-bottom:8px;background:rgba(255,255,255,.07);color:#e2e8f0;border:0;padding:10px;border-radius:8px;font:700 12px system-ui;cursor:pointer;">Switch Team</button>
      <button id="pause-tutorial" style="display:block;width:100%;margin-bottom:8px;background:rgba(255,255,255,.07);color:#e2e8f0;border:0;padding:10px;border-radius:8px;font:700 12px system-ui;cursor:pointer;">Replay Tutorial</button>
      <button id="pause-resume" style="display:block;width:100%;background:#3b82f6;color:#fff;border:0;padding:10px;border-radius:8px;font:700 13px system-ui;cursor:pointer;">Close [Esc]</button>
    `;
    parent.appendChild(this.el);
    this.el.querySelector<HTMLButtonElement>('#pause-resume')!.addEventListener('click', () => this.hide());
    this.el.querySelector<HTMLButtonElement>('#pause-tutorial')!.addEventListener('click', () => { this.hide(); this.onTutorial(); });
    this.el.querySelector<HTMLButtonElement>('#pause-switch')!.addEventListener('click', () => { this.hide(); this.onSwitchTeam(); });
    this.el.querySelector<HTMLButtonElement>('#pause-settings')!.addEventListener('click', () => { this.onSettings(); });
    this.el.querySelector<HTMLButtonElement>('#pause-lobby')!.addEventListener('click', () => { this.hide(); this.onGoToLobby(); });
    this.el.querySelector<HTMLButtonElement>('#pause-invite')!.addEventListener('click', () => { this.onCopyInvite(); });
  }

  show(): void {
    this.el.style.display = 'block';
    // Force layout so the transition picks up the from-state.
    void this.el.offsetWidth;
    this.el.style.opacity = '1';
    this.el.style.transform = 'translate(-50%, -50%) scale(1)';
    document.body.classList.add('smp-pause-open');
  }

  hide(): void {
    this.el.style.opacity = '0';
    this.el.style.transform = 'translate(-50%, -50%) scale(.96)';
    document.body.classList.remove('smp-pause-open');
    // Defer display:none until the transition completes so the
    // fade-out actually plays.
    setTimeout(() => {
      // Re-check open state — show() might have run during the wait.
      if (this.el.style.opacity === '0') this.el.style.display = 'none';
    }, MENU_ANIM_MS + 30);
    this.onResume();
  }

  isOpen(): boolean {
    return this.el.style.display !== 'none' && this.el.style.opacity !== '0';
  }

  setInLobby(inLobby: boolean): void {
    // Leave-match + invite-link buttons are only meaningful in-match.
    const leaveBtn  = this.el.querySelector<HTMLButtonElement>('#pause-lobby');
    const inviteBtn = this.el.querySelector<HTMLButtonElement>('#pause-invite');
    const display = inLobby ? 'none' : 'block';
    if (leaveBtn)  leaveBtn.style.display  = display;
    if (inviteBtn) inviteBtn.style.display = display;
  }
}
